import { useMotionDisabled } from "@gladis/ui/motion";
import { useTheme, type ResolvedTheme } from "@gladis/ui/theme";
import { lazy, Suspense, type ReactNode } from "react";

/**
 * Split into its own chunk, because it is the single heaviest thing the app ships and the least
 * urgent: a raymarching shader plus `ogl`, drawing decoration nobody is waiting on. Keeping it in
 * the main bundle meant the sign-in form could not paint until the WebGL renderer had downloaded.
 *
 * `lazy` at module scope, not inside the component — calling it per render would create a new lazy
 * component on every pass and remount the canvas each time, which is exactly the WebGL context
 * churn that mounting at the root exists to avoid.
 *
 * A named export needs the shape adapting: `lazy` wants a module whose `default` is the component.
 */
const GradientWaves = lazy(() =>
  import("./gradient-waves.js").then((module) => ({ default: module.GradientWaves })),
);

/**
 * The animated background, behind every page.
 *
 * Mounted ONCE on the root route rather than per page. A WebGL context is expensive to create and
 * browsers cap how many can be live, so mounting it per route would rebuild the context on every
 * navigation and restart the animation from a standstill. Mounted at the root, the waves simply
 * keep rolling as you move between pages.
 *
 * It is `pointer-events: none` and `aria-hidden` — decoration that must never take a click or a
 * screen-reader's attention. The colours are the shader primitives in `styles/tokens.css`, where
 * the still fallback repaints the same gradient in CSS.
 */
/**
 * The shader's three colours, per theme.
 *
 * THESE ARE LITERALS AND THEY ARE NOT THE SOURCE OF TRUTH. `--backdrop-horizon`, `--backdrop-wave`
 * and `--backdrop-crest` in `@gladis/ui`'s `tokens.css` are, and `background-tokens.test.ts` asserts
 * this table against that file for both themes.
 *
 * The alternative was reading the custom properties off `document.documentElement` at render time,
 * which removes the duplication and adds two worse problems: a synchronous layout read on the
 * first-paint path, and a value of `""` under jsdom — where no stylesheet is loaded — which
 * `hexToRgb` turns into white. A checked copy fails in CI; an unchecked read fails on screen.
 */
const BACKDROP: Record<ResolvedTheme, { horizon: string; wave: string; crest: string }> = {
  dark: { horizon: "#2563eb", wave: "#7c3aed", crest: "#7c3aed" },
  light: { horizon: "#bfdbfe", wave: "#c4b5fd", crest: "#ddd6fe" },
};

export function AppBackground(): ReactNode {
  const motionDisabled = useMotionDisabled();
  /*
   * `resolved`, never `choice` — the shader needs a colour, and `system` is not one.
   *
   * A theme change re-renders this component with new props, and `GradientWaves` updates the three
   * uniforms in place rather than rebuilding the renderer. So switching theme recolours the waves
   * mid-roll instead of dropping the WebGL context and restarting the animation from a standstill,
   * which is the same property mounting this at the root buys for navigation.
   */
  const { resolved } = useTheme();
  const backdrop = BACKDROP[resolved];

  /*
   * The fallback drops the shader entirely for a CSS gradient in the same colours, rather than
   * freezing the shader in place.
   *
   * Freezing was the first attempt and it is not enough: the cost is not the animation, it is the
   * WebGL context itself. Someone who asked for less motion gains nothing from a still image being
   * drawn sixty times a second by a raymarcher, either.
   */
  if (motionDisabled) return <div className="app-background app-background--still" />;

  /*
   * The CSS gradient stands in while the shader chunk downloads — the same one the reduced-motion
   * path uses, in the same colours. So the page is never briefly flat black waiting on a network
   * request, and on a slow connection the still gradient simply IS the background until the waves
   * are ready to take over.
   */
  return (
    <Suspense fallback={<div className="app-background app-background--still" />}>
      <div className="app-background">
        <GradientWaves
          horizonColor={backdrop.horizon}
          waveColor={backdrop.wave}
          crestColor={backdrop.crest}
        />
      </div>
    </Suspense>
  );
}
